import { useMutation } from '@apollo/client';
import {
  AgFinancingInput,
  AgFinancingPhaseStatus,
  BackgroundCheck,
  BackgroundCheckStatus,
  Phase1 as IPhase1,
  Phase1Input
} from '@indigo-ag/schema';
import i18next from 'core/i18n';
import { ExecutionResult } from 'graphql';
import { Dictionary } from 'lodash';
import React, { useContext } from 'react';

import { UserContext } from 'core/components/GateWay/GateWay';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { upsertFinancingAgFinancing } from 'features/brazil-grower/queries';
import { LoadingIndicator, Phase2Util, brazil } from 'shared';
import { NoContactsFound } from 'shared/components/NoResultsFound/NoContactsFounds';
import { getUniqueContactRoles } from '../utils/backgroundCheck.util';
import { DecisionNotificationSection } from './phase1/DecisionNotificationSection';
import { StatusActionBar } from './phase1/StatusActionBar';

import { isValidCpf } from '@brazilian-utils/validators';
import { usePhase1Menu } from '../../Timeline/context/Phase1MenuContext';
import { SerasaBacenSection } from './phase1/SerasaBacenSection';
import { Divider } from '@indigo-ag/component-module';
import { BackgroundCheckSection } from './phase1/BackgroundCheckSection';
import { FundsApprovalSection } from './phase1/FundsApprovalSection';
import { getRiskClassificationList, getSerasaBacenList } from './Phase1.utils';

interface IPhase1Context {
  updatePhase: (newPhaseInfo: Partial<Phase1Input>) => Promise<ExecutionResult<any>>;
}

export const Phase1Context = React.createContext<IPhase1Context>({
  updatePhase: () => Promise.resolve({})
});
interface Phase1Props {
  phaseInfo?: IPhase1;
}

export const Phase1: React.FunctionComponent<Phase1Props> = ({ phaseInfo }) => {
  /**
   * Context
   */
  const { agFinancingId, contactRolesBackgroundChecksQuery, agFinance } = useContext(FinanceProcessContext);
  const { fullName } = useContext(UserContext);

  const { selectedItem } = usePhase1Menu();

  /**
   * Mutations
   */
  const [upsertFinancingAgFinancingFn] = useMutation(upsertFinancingAgFinancing);

  const hasPromissoryNote = Phase2Util.hasPromissoryNote(agFinance);
  const updatePhase = (updatedPhase: Partial<Phase1Input>) => {
    let newAgFinancingData: Partial<AgFinancingInput> = {
      id: agFinancingId,
      phase1: {
        ...updatedPhase
      }
    };

    if (updatedPhase.status === AgFinancingPhaseStatus.CLEAR) {
      newAgFinancingData = Phase2Util.autoStartPhase2(
        newAgFinancingData,
        true,
        hasPromissoryNote, // * Start collateral if does have NP. Geo-ref not needed
        !hasPromissoryNote, // * Start Geo-ref if doesn't have NP
        fullName
      );
    }

    return upsertFinancingAgFinancingFn({
      variables: {
        data: newAgFinancingData
      }
    });
  };

  if (contactRolesBackgroundChecksQuery?.loading) {
    return <LoadingIndicator />;
  } else if (contactRolesBackgroundChecksQuery?.error) {
    return i18next.t('error');
  }

  const backgroundChecks: BackgroundCheck[] =
    contactRolesBackgroundChecksQuery?.data?.backgroundCheckGroup?.backgroundChecks ?? [];
  const contactRoles = getUniqueContactRoles(contactRolesBackgroundChecksQuery?.data?.opportunityContactRoles ?? []);
  const serasaReports = contactRolesBackgroundChecksQuery?.data?.serasaReport?.reports ?? [];
  const bacenReports = contactRolesBackgroundChecksQuery?.data?.bacenReport?.reports ?? [];
  const riskClassifications =
    contactRolesBackgroundChecksQuery?.data?.contactsRiskClassification?.classifications ?? [];
  const clientIsAContact = contactRoles?.some(item => item.contact.taxIdentifier === agFinance?.grower?.cpfOrCnpj);

  if (agFinance?.grower && !clientIsAContact) {
    contactRoles.push({
      id: agFinance.grower.id,
      contact: {
        id: agFinance.grower.id,
        name: agFinance.grower.name,
        taxIdentifier: agFinance.grower.cpfOrCnpj
      },
      contactId: agFinance.grower.id,
      isPrimary: false,
      opportunityId: '',
      role: 'Cliente'
    });
  }

  const backgroundChecksByTin = backgroundChecks.reduce(
    (result: Dictionary<BackgroundCheck>, backgroundCheck: BackgroundCheck) => {
      result[brazil.cleanCpfCnpjValue(backgroundCheck.taxIdentifier)] = backgroundCheck;
      return result;
    },
    {}
  );

  const serasaBacenList = getSerasaBacenList(contactRoles, serasaReports, bacenReports);
  const riskClassificationList = getRiskClassificationList(contactRoles, riskClassifications);
  return (
    <Phase1Context.Provider value={{ updatePhase }}>
      <StatusActionBar
        canEscalate={
          phaseInfo?.status !== AgFinancingPhaseStatus.CLEAR &&
          phaseInfo?.status !== AgFinancingPhaseStatus.DENIED &&
          phaseInfo?.status !== AgFinancingPhaseStatus.AWAITING_MANAGER_APPROVAL
        }
        canSetDecision={backgroundChecks
          .filter(c => isValidCpf(c.taxIdentifier))
          .every(c => c.status === BackgroundCheckStatus.COMPLETE)}
        phaseStatus={phaseInfo?.status}
      />
      {!!phaseInfo && (
        <DecisionNotificationSection
          decidedAt={phaseInfo.decisionAt}
          decidedBy={phaseInfo.decisionBy}
          notes={phaseInfo.notes}
          phaseStatus={phaseInfo.status}
        />
      )}
      <Divider style={{ margin: '16px 0' }} />
      {selectedItem === 'backgroundCheck' && (
        <BackgroundCheckSection
          contactRoles={contactRoles.map(role => ({
            ...role,
            contact: {
              name: role.contact.name,
              taxIdentifier: role.contact.taxIdentifier ?? ''
            }
          }))}
          backgroundChecksByTin={backgroundChecksByTin}
        />
      )}
      {selectedItem === 'preAnalysisSerasa' && <SerasaBacenSection serasaBacenList={serasaBacenList} />}
      {selectedItem === 'preAnalysisFunds' && <FundsApprovalSection riskClassificationList={riskClassificationList} />}

      {contactRoles.length === 0 && <NoContactsFound />}
    </Phase1Context.Provider>
  );
};
